import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/EditProject.css';

function EditProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectValue, setProjectValue] = useState(0);
  const [labour, setLabour] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [completionDate, setCompletionDate] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch project data
        const docRef = doc(db, "projects", projectId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const projectData = docSnap.data();
          setProject({ id: docSnap.id, ...projectData });
          setProjectValue(projectData.projectValue || 0);
          setInvoices(projectData.invoices || []);
          setCompletionDate(projectData.completionDate || '');
        } else {
          console.log("No such project!");
          navigate('/projects');
        }

        // Fetch subcontractors
        const querySnapshot = await getDocs(collection(db, "subcontractors"));
        const subcontractorsList = [];
        querySnapshot.forEach((doc) => {
          subcontractorsList.push({ id: doc.id, ...doc.data() });
        });
        setSubcontractors(subcontractorsList);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, navigate]);

  // Derive labour from timesheets for this project
  useEffect(() => {
    const deriveLabourFromTimesheets = async () => {
      if (!projectId || subcontractors.length === 0) return;
      try {
        const qTimes = query(
          collection(db, 'timesheets'),
          where('projectId', '==', projectId)
        );
        const snap = await getDocs(qTimes);
        const countBySub = new Map(); // subcontractorId -> days count
        snap.forEach((d) => {
          const data = d.data();
          const subId = data.subcontractorId;
          if (!subId) return;
          countBySub.set(subId, (countBySub.get(subId) || 0) + 1);
        });
        const derived = Array.from(countBySub.entries()).map(([subId, days]) => {
          const sub = subcontractors.find((s) => s.id === subId);
          const dayRate = sub?.dayRate || 0;
          return {
            days: Number(days),
            subcontractor: sub ? { id: sub.id, name: sub.name, dayRate: dayRate } : { id: subId, name: subId, dayRate: 0 },
            cost: Number(days) * Number(dayRate)
          };
        });
        setLabour(derived);
      } catch (e) {
        console.error('Failed to derive labour from timesheets', e);
      }
    };

    deriveLabourFromTimesheets();
  }, [projectId, subcontractors]);

  const saveProject = async () => {
    try {
      setIsLoading(true);
      const totalLabourCost = Number(labour.reduce((sum, entry) => sum + entry.cost, 0).toFixed(2));
      const totalInvoices = Number(invoices.reduce((sum, invoice) => sum + Number(invoice.amount), 0).toFixed(2));
      const totalCosts = Number((totalLabourCost + totalInvoices).toFixed(2));
      const profit = Number((projectValue - totalCosts).toFixed(2));

      const projectData = {
        name: project.name,
        projectValue,
        labour,
        invoices,
        totalLabourCost,
        totalInvoices,
        totalCosts,
        profit,
        completionDate,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, "projects", projectId), projectData);
      setShowSaveConfirm(true);
    } catch (error) {
      console.error("Error saving project:", error);
      alert('Failed to save project. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProject = async () => {
    try {
      setIsLoading(true);
      await deleteDoc(doc(db, "projects", projectId));
      navigate('/projects');
    } catch (error) {
      console.error("Error deleting project:", error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const addInvoice = (amount, supplier, invoiceNumber) => {
    setInvoices([...invoices, {
      amount: Number(amount),
      supplier,
      invoiceNumber,
      id: Date.now()
    }]);
  };

  const deleteInvoice = (id) => {
    setInvoices(invoices.filter(invoice => invoice.id !== id));
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading project...</div>;
  }

  if (!project) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Project not found.</p>
        <Link to="/projects" className="back-button">← Back to Cost Tracking</Link>
      </div>
    );
  }

  const totalLabourCost = Number(labour.reduce((sum, entry) => sum + entry.cost, 0).toFixed(2));
  const totalInvoiceAmount = Number(invoices.reduce((sum, invoice) => sum + Number(invoice.amount), 0).toFixed(2));
  const totalCosts = Number((totalLabourCost + totalInvoiceAmount).toFixed(2));
  const profit = Number((projectValue - totalCosts).toFixed(2));

  return (
    <div className="edit-project-container">
      {/* Left Sidebar - Subcontractors */}
      <div className="sidebar-subcontractors">
        <h2>Subcontractors</h2>
        <select 
          id="sidebarSubcontractorSelect"
          className="form-select"
        >
          <option value="">Select Subcontractor</option>
          {subcontractors.map(sub => (
            <option key={sub.id} value={JSON.stringify(sub)}>
              {sub.name} - £{sub.dayRate}/day
            </option>
          ))}
        </select>
        
        <button 
          onClick={() => alert('Add subcontractor in Subcontractors page')}
          className="btn btn-primary"
        >
          Add New Subcontractor
        </button>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Project Header */}
        <div className="project-header">
          <div className="project-header-left">
            <Link to="/dashboard" className="home-button">
              ← Home
            </Link>
            <Link to="/projects" className="back-button">
              ← Cost Tracking
            </Link>
            <h1>{project.name}</h1>
          </div>
          <div className="input-group">
            <button
              onClick={saveProject}
              disabled={isLoading}
              className="btn btn-primary"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
              className="btn btn-danger"
            >
              Delete Project
            </button>
          </div>
        </div>

        {/* Calculator Content */}
        <div className="calculator-content">
          {/* Main Calculator Area */}
          <div className="calculator-main">
            {/* Project Value */}
            <div className="card">
              <h2 className="section-title">Project Value</h2>
              <div className="input-group">
                <input 
                  type="number"
                  value={projectValue || ''}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : 0;
                    setProjectValue(value);
                  }}
                  className="form-input"
                  placeholder="Enter project value"
                />
              </div>
            </div>

            {/* Completion Date */}
            <div className="card">
              <h2 className="section-title">Completion Date</h2>
              <div className="input-group">
                <input 
                  type="date"
                  value={completionDate}
                  onChange={(e) => setCompletionDate(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            {/* Labour Section */}
            <div className="card">
              <h2 className="section-title">Labour (auto from Timesheet)</h2>
              <p className="text-muted" style={{ marginTop: 0 }}>
                This section is derived from Timesheet calendar entries for this project.
              </p>

              {/* Labour List */}
              <div className="card">
                {labour.map((entry, index) => (
                  <div key={index} className="input-group">
                    <div>
                      <strong>{entry.subcontractor.name}</strong>
                      <span>•</span>
                      <span>{entry.days} days</span>
                      <span>•</span>
                      <span>£{entry.cost}</span>
                    </div>
                  </div>
                ))}
                {labour.length === 0 && (
                  <p className="text-center text-muted">No labour entries yet</p>
                )}
                <div className="input-group">
                  <strong>Total Labour Cost: £{totalLabourCost}</strong>
                </div>
              </div>
            </div>

            {/* Invoices Section */}
            <div className="card">
              <h2 className="section-title">Invoices</h2>
              <div className="input-group">
                <input 
                  type="text"
                  placeholder="Supplier Name"
                  id="supplierName"
                  className="form-input"
                />
                <input 
                  type="text"
                  placeholder="Invoice Number"
                  id="invoiceNumber"
                  className="form-input"
                />
                <input 
                  type="number"
                  placeholder="Amount"
                  id="invoiceAmount"
                  className="form-input"
                />
                <button 
                  onClick={() => {
                    const amount = document.getElementById('invoiceAmount').value;
                    const supplier = document.getElementById('supplierName').value;
                    const invoiceNumber = document.getElementById('invoiceNumber').value;
                    if (amount && supplier && invoiceNumber) {
                      addInvoice(amount, supplier, invoiceNumber);
                      document.getElementById('invoiceAmount').value = '';
                      document.getElementById('supplierName').value = '';
                      document.getElementById('invoiceNumber').value = '';
                    }
                  }}
                  className="btn btn-primary"
                >
                  Add Invoice
                </button>
              </div>

              {/* Invoices List */}
              <div className="card">
                {invoices.map((invoice, index) => (
                  <div key={invoice.id} className="input-group">
                    <div>
                      <strong>{invoice.supplier}</strong>
                      <span>•</span>
                      <span>#{invoice.invoiceNumber}</span>
                      <span>•</span>
                      <span>£{invoice.amount}</span>
                    </div>
                    <button 
                      onClick={() => deleteInvoice(invoice.id)}
                      className="btn btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                ))}
                {invoices.length === 0 && (
                  <p className="text-center text-muted">No invoices yet</p>
                )}
                <div className="input-group">
                  <strong>Total Invoices: £{totalInvoiceAmount}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Right Summary Sidebar */}
          <div className="summary-sidebar">
            <div className="card summary-card">
              <h2 className="section-title">Project Summary</h2>
              
              <div className="summary-content">
                {completionDate && (
                  <div className="summary-item">
                    <strong>Completion Date:</strong>
                    <span>{new Date(completionDate).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="summary-item">
                  <strong>Project Value:</strong>
                  <span>£{projectValue}</span>
                </div>
                
                <div className="summary-item">
                  <strong>Total Labour:</strong>
                  <span>£{totalLabourCost}</span>
                </div>
                
                <div className="summary-item">
                  <strong>Total Invoices:</strong>
                  <span>£{totalInvoiceAmount}</span>
                </div>
                
                <div className="summary-item">
                  <strong>Total Costs:</strong>
                  <span>£{totalCosts}</span>
                </div>
                
                <div className="summary-item">
                  <strong>Profit:</strong>
                  <span>£{profit}</span>
                </div>

                <div className="summary-item">
                  <strong>Margin:</strong>
                  <span>{projectValue ? ((profit / projectValue) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirmation">
          <div className="delete-confirmation-content">
            <h2>Delete Project?</h2>
            <p>Are you sure you want to delete this project? This action cannot be undone.</p>
            <div className="input-group">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={deleteProject}
                className="btn btn-danger"
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Confirmation Modal */}
      {showSaveConfirm && (
        <div 
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSaveConfirm(false);
            }
          }}
          className="save-confirmation"
        >
          <div className="save-confirmation-content">
            <div className="icon">
              <span>✓</span>
            </div>
            <h2>Success!</h2>
            <p>Project saved successfully.</p>
            <button
              onClick={() => setShowSaveConfirm(false)}
              className="btn btn-primary"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default EditProject; 